import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ClearIcon from '@mui/icons-material/Clear';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import SortByAlphaIcon from '@mui/icons-material/SortByAlpha';
import {
    Box,
    Chip,
    CircularProgress,
    Collapse,
    Grid,
    IconButton,
    InputAdornment,
    Stack,
    Tab,
    Tabs,
    TextField,
    Theme,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import { useDialog } from 'muibox';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import useAxios from '../../hooks/useAxios';
import { OnlyFansUserListsState } from '../../hooks/useInsightsUserProfile';
import { SettingsContext } from '../../store/SettingsContext';
import { UserContext } from '../../store/UserContext';
import { handleHttpError, stripHtmlAndConvertParagraphs } from '../../utils/common';
import MessageUsersTagCreatorsList from '../services/messageUsers/sendMessage/MessageUsersTagCreatorsList';
import MessageUsersTagReleaseFormsList from '../services/messageUsers/sendMessage/MessageUsersTagReleaseFormsList';

export namespace OnlyFansReleaseForms {
    export interface Response {
        items: Item[];
        hasMore: boolean;
    }

    export interface Item {
        id: number;
        type: Type;
        name: string;
        code: string;
        date: Date;
        user_partner_id?: number;
        signers_count: number;
        signed: number;
        approve_date: Date | null;
        last_changed: Date | null;
        status: Status;
        hiddenForRf: boolean;
        user_name?: string;
        hasUser?: boolean;
        signature?: string;
    }

    export enum Status {
        Approved = 'approved',
        Declined = 'declined',
        Failed = 'failed',
    }

    export enum Type {
        Link = 'link',
        Doc = 'doc',
        User = 'user',
    }
}

export namespace OnlyFansTaggedFriends {
    export interface Response {
        items: Item[];
        hasMore: boolean;
    }

    export interface Item {
        id: number;
        name: string;
        type: Type;
        user: User;
    }

    export enum Type {
        User = 'user',
    }

    export interface User {
        view: View;
        avatar: null | string;
        avatarThumbs: AvatarThumbs | null;
        header: null | string;
        headerSize: HeaderSize | null;
        headerThumbs: HeaderThumbs | null;
        id: number;
        name: string;
        username: string;
        canLookStory: boolean;
        canCommentStory: boolean;
        hasNotViewedStory: boolean;
        isVerified: boolean;
        canPayInternal: boolean;
        hasScheduledStream: boolean;
        hasStream: boolean;
        hasStories: boolean;
        tipsEnabled: boolean;
        tipsTextEnabled: boolean;
        tipsMin: number;
        tipsMinInternal: number;
        tipsMax: number;
        canEarn: boolean;
        canAddSubscriber: boolean;
        subscribePrice: number;
        isPaywallRequired?: boolean;
        unprofitable: boolean;
        listsStates?: OnlyFansUserListsState[];
        isMuted?: boolean;
        isRestricted?: boolean;
        canRestrict?: boolean;
        subscribedBy?: boolean | null;
        subscribedByExpire?: boolean | null;
        subscribedByExpireDate?: Date | null;
        subscribedByAutoprolong?: boolean | null;
        subscribedIsExpiredNow?: boolean | null;
        currentSubscribePrice?: number | null;
        subscribedOn: boolean | null;
        subscribedOnExpiredNow?: boolean | null;
        subscribedOnDuration?: null | string;
        ivStatus: IvStatus | null;
        isFromGuest: boolean;
        hiddenForRf: boolean;
        callPrice: number;
        lastSeen: Date | null;
        canReport: boolean;
        displayName?: string;
        notice?: string;
        hasStripe?: boolean;
        isStripeExist?: boolean;
        subscriptionBundles?: SubscriptionBundle[];
        canSendChatToAll?: boolean;
        creditsMin?: number;
        creditsMax?: number;
        isPaywallPassed?: boolean;
        listsSort?: ListsSort;
        listsSortOrder?: string;
        canCreateLists?: boolean;
    }

    export interface AvatarThumbs {
        c50: string;
        c144: string;
    }

    export interface HeaderSize {
        width: number;
        height: number;
    }

    export interface HeaderThumbs {
        w480: string;
        w760: string;
    }

    export enum IvStatus {
        Approved = 'approved',
        Expired = 'expired',
    }

    export enum ListsSort {
        Bookmarks = 'bookmarks',
        CloseFriends = 'close_friends',
        Custom = 'custom',
        Fans = 'fans',
        Following = 'following',
        Friends = 'friends',
        Muted = 'muted',
        Recent = 'recent',
        Tagged = 'tagged',
    }

    export interface ListsState {
        id: number;
        type: ListsSort;
        name: string;
        hasUser: boolean;
        canAddUser: boolean;
    }

    export interface SubscriptionBundle {
        id: number;
        discount: number;
        duration: number;
        price: number;
        canBuy: boolean;
    }

    export enum View {
        S = 's',
    }
}

// Memoized TabPanel component to prevent unnecessary re-renders
const CustomTabPanel = React.memo(({ children, value, index, ...other }: { children?: React.ReactNode; index: number; value: number }) => {
    return (
        <div role="tabpanel" hidden={value !== index} id={`simple-tabpanel-${index}`} aria-labelledby={`simple-tab-${index}`} {...other}>
            {value === index && <Box sx={{ p: 0, pt: 1, bgcolor: 'transparent' }}>{children}</Box>}
        </div>
    );
});

// Memoized ActionButtons component
const ActionButtons = React.memo(
    ({
        onSearch,
        onSortToggle,
        sortEnabled,
        onClearSelection,
        onRefetch,
        disabled,
    }: {
        onSearch: () => void;
        onSortToggle: () => void;
        sortEnabled: boolean;
        onClearSelection: () => void;
        onRefetch: () => void;
        disabled: boolean;
    }) => {
        return (
            <Stack direction="row" spacing={0} alignItems="center" justifyContent="center">
                <Tooltip title="Search">
                    <IconButton onClick={onSearch} color="primary" disabled={disabled}>
                        <SearchIcon fontSize="large" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Sort alphabetically">
                    <IconButton onClick={onSortToggle} color={sortEnabled ? 'primary' : 'secondary'} disabled={disabled}>
                        <SortByAlphaIcon fontSize="large" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Clear Selection">
                    <IconButton color="error" onClick={onClearSelection} disabled={disabled}>
                        <ClearIcon fontSize="large" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Reload from OnlyFans">
                    <IconButton color="secondary" onClick={onRefetch} disabled={disabled}>
                        <RefreshIcon fontSize="large" />
                    </IconButton>
                </Tooltip>
            </Stack>
        );
    },
);

// Memoized SelectedChips component
const SelectedChips = React.memo(({ items, onDelete }: { items: { id: number; name: string }[]; onDelete: (id: number) => void }) => {
    if (items.length === 0) {
        return <Typography variant="caption">No items selected.</Typography>;
    }

    return (
        <Stack direction="row" flexWrap="wrap" alignItems="center" spacing={2} rowGap={1} justifyContent="flex-start">
            {items.map(item => (
                <Chip key={item.id} label={stripHtmlAndConvertParagraphs(item.name)} onDelete={() => onDelete(item.id)} />
            ))}
        </Stack>
    );
});

// Search Results Chips component for Tagged Friends
const SearchResultChipsTaggedFriends = React.memo(
    ({
        items,
        onAdd,
        selectedItems,
    }: {
        items: OnlyFansTaggedFriends.Item[];
        onAdd: (id: number, name: string) => void;
        selectedItems: { id: number; name: string }[];
    }) => {
        if (items.length === 0) {
            return (
                <Typography variant="caption" color="text.secondary">
                    No search results found.
                </Typography>
            );
        }

        return (
            <Stack direction="row" flexWrap="wrap" alignItems="center" spacing={1} rowGap={1} justifyContent="flex-start">
                {items.map(item => {
                    const isSelected = selectedItems.some(selected => selected.id === item.id);
                    return (
                        <Chip
                            key={item.id}
                            label={`${stripHtmlAndConvertParagraphs(item.name)} (@${item.user.username})`}
                            onClick={() => onAdd(item.id, item.name)}
                            variant={isSelected ? 'filled' : 'outlined'}
                            color={isSelected ? 'primary' : 'default'}
                            disabled={isSelected}
                            clickable={!isSelected}
                        />
                    );
                })}
            </Stack>
        );
    },
);

// Search Results Chips component for Release Forms
const SearchResultChipsReleaseForms = React.memo(
    ({
        items,
        onAdd,
        selectedItems,
    }: {
        items: OnlyFansReleaseForms.Item[];
        onAdd: (id: number, name: string) => void;
        selectedItems: { id: number; name: string }[];
    }) => {
        if (items.length === 0) {
            return (
                <Typography variant="caption" color="text.secondary">
                    No search results found.
                </Typography>
            );
        }

        return (
            <Stack direction="row" flexWrap="wrap" alignItems="center" spacing={1} rowGap={1} justifyContent="flex-start">
                {items.map(item => {
                    const isSelected = selectedItems.some(selected => selected.id === item.id);
                    return (
                        <Chip
                            key={item.id}
                            label={stripHtmlAndConvertParagraphs(item.name)}
                            onClick={() => onAdd(item.id, item.name)}
                            variant={isSelected ? 'filled' : 'outlined'}
                            color={isSelected ? 'primary' : 'default'}
                            disabled={isSelected}
                            clickable={!isSelected}
                        />
                    );
                })}
            </Stack>
        );
    },
);

// Loading indicator component
const LoadingIndicator = React.memo(({ type }: { type: string }) => (
    <Box textAlign={'center'}>
        <Typography variant="caption">
            Retrieving your {type}...
            <br />
            <br />
            This might take a while depending on how many {type.toLowerCase()} you have and OnlyFans servers speed 🤷‍♂️
            <br />
            <br />
            <CircularProgress size={24} />
        </Typography>
    </Box>
));

function a11yProps(index: number) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

type Props = {
    subscriber: any;
    tagCreators: boolean;
    setTagCreators: (tagCreators: boolean) => void;
    taggedCreators: {
        id: number;
        name: string;
    }[];
    setTaggedCreators: React.Dispatch<
        React.SetStateAction<
            {
                id: number;
                name: string;
            }[]
        >
    >;
    taggedReleaseForms: {
        id: number;
        name: string;
    }[];
    setTaggedReleaseForms: React.Dispatch<
        React.SetStateAction<
            {
                id: number;
                name: string;
            }[]
        >
    >;
    session?: any;
    disabled?: boolean;
    /** Tri-state support: ids rendered as indeterminate, i.e. tags only some of the targets carry */
    indeterminateCreatorIds?: Set<number>;
    indeterminateReleaseFormIds?: Set<number>;
    /** When set, list clicks are delegated here instead of the plain toggle so the owner can cycle a tri-state */
    onCreatorListItemClicked?: (item: { id: number; name: string }) => void;
    onReleaseFormListItemClicked?: (item: { id: number; name: string }) => void;
};

const OnlyFansTagCreatorsSelector = (props: Props) => {
    const {
        subscriber,
        taggedCreators,
        setTaggedCreators,
        taggedReleaseForms,
        setTaggedReleaseForms,
        disabled = false,
        indeterminateCreatorIds,
        indeterminateReleaseFormIds,
        onCreatorListItemClicked,
        onReleaseFormListItemClicked,
    } = props;
    const userContext = useContext(UserContext);
    const settingsContext = useContext(SettingsContext);
    const theme: Theme = useTheme();
    const axios = useAxios();
    const dialog = useDialog();

    const [releaseFormsQuery, setReleaseFormsQuery] = useState<string>('');

    const [sortTaggedFriends, setSortTaggedFriends] = useState<boolean>(false);
    const [sortReleaseForms, setSortReleaseForms] = useState<boolean>(false);

    const [tabValue, setTabValue] = useState<number>(0);

    // Search states
    const [showCreatorsSearch, setShowCreatorsSearch] = useState<boolean>(false);
    const [showReleaseFormsSearch, setShowReleaseFormsSearch] = useState<boolean>(false);
    const [creatorsSearchQuery, setCreatorsSearchQuery] = useState<string>('');
    const [releaseFormsSearchQuery, setReleaseFormsSearchQuery] = useState<string>('');

    const taggedCreatorsCount = taggedCreators ? taggedCreators.length : 0;
    const taggedReleaseFormsCount = taggedReleaseForms ? taggedReleaseForms.length : 0;
    const totalTagged = taggedCreatorsCount + taggedReleaseFormsCount;

    // Debounce search queries
    useEffect(() => {
        const timer = setTimeout(() => {
            setReleaseFormsQuery(releaseFormsSearchQuery);
        }, 300);

        return () => clearTimeout(timer);
    }, [releaseFormsSearchQuery]);

    // Memoize API query functions to prevent recreation on render
    const fetchTaggedFriends = useCallback(async () => {
        if (!subscriber || !userContext.jwtToken || !settingsContext.apiKey) return null;

        const query = `${settingsContext.routes.onlyFans.base}${subscriber._id}/taggedFriends`;

        try {
            const response = await axios.get(query);

            if (response.status === 200) {
                return response.data as OnlyFansTaggedFriends.Item[];
            } else {
                return null;
            }
        } catch (error) {
            console.error(error);
            handleHttpError(error, dialog);
            return null;
        }
    }, [subscriber, userContext.jwtToken, settingsContext.apiKey, settingsContext.routes.onlyFans.base, dialog]);

    // Search creators function
    const fetchCreatorsSearch = useCallback(
        async (searchQuery: string) => {
            if (!subscriber || !userContext.jwtToken || !settingsContext.apiKey || !searchQuery.trim()) return [];

            const query = `${settingsContext.routes.onlyFans.base}${subscriber._id}/taggedFriends?query=${encodeURIComponent(searchQuery)}`;

            try {
                const response = await axios.get(query);

                if (response.status === 200) {
                    return response.data as OnlyFansTaggedFriends.Item[];
                } else {
                    return [];
                }
            } catch (error) {
                console.error(error);
                handleHttpError(error, dialog);
                return [];
            }
        },
        [subscriber, userContext.jwtToken, settingsContext.apiKey, settingsContext.routes.onlyFans.base, axios, dialog],
    );

    const {
        data: taggedFriends,
        isLoading: taggedFriendsLoading,
        isFetching: taggedFriendsFetching,
        refetch: refetchTaggedFriends,
    } = useQuery(
        ['taggedFriends', subscriber],
        () => {
            if (subscriber) {
                return fetchTaggedFriends();
            }
        },
        {
            refetchOnWindowFocus: false,
            staleTime: Infinity,
            enabled: subscriber ? true : false,
        },
    );

    // Creators search query
    const {
        data: creatorsSearchResults,
        isLoading: creatorsSearchLoading,
        isFetching: creatorsSearchFetching,
    } = useQuery(
        ['creatorsSearch', subscriber, creatorsSearchQuery],
        () => {
            if (subscriber && creatorsSearchQuery.trim()) {
                return fetchCreatorsSearch(creatorsSearchQuery);
            }
            return [];
        },
        {
            refetchOnWindowFocus: false,
            staleTime: 30000,
            enabled: !!(subscriber && creatorsSearchQuery.trim()),
        },
    );

    const fetchReleaseForms = useCallback(
        async (releaseFormsQuery: string | null) => {
            if (!subscriber || !userContext.jwtToken || !settingsContext.apiKey) return null;

            const query = `${settingsContext.routes.onlyFans.base}${subscriber._id}/releaseForms${
                releaseFormsQuery ? `?query=${encodeURIComponent(releaseFormsQuery)}` : ''
            }`;

            try {
                const response = await axios.get(query);

                if (response.status === 200) {
                    return response.data as OnlyFansReleaseForms.Item[];
                } else {
                    return null;
                }
            } catch (error) {
                console.error(error);
                handleHttpError(error, dialog);
                return null;
            }
        },
        [subscriber, userContext.jwtToken, settingsContext.apiKey, settingsContext.routes.onlyFans.base, axios, dialog],
    );

    const {
        data: releaseForms,
        isLoading: releaseFormsLoading,
        isFetching: releaseFormsFetching,
        refetch: refetchReleaseForms,
    } = useQuery(
        ['releaseForms', subscriber, releaseFormsQuery],
        () => {
            if (subscriber) {
                return fetchReleaseForms(releaseFormsQuery);
            }
        },
        {
            refetchOnWindowFocus: false,
            staleTime: 30000,
            enabled: subscriber ? true : false,
        },
    );

    // Memoize sorted data
    const sortedTaggedFriends = useMemo(() => {
        if (!taggedFriends) return [];

        if (sortTaggedFriends) {
            return [...taggedFriends].sort((a, b) => {
                const x = a.name.toLowerCase();
                const y = b.name.toLowerCase();
                return x < y ? -1 : x > y ? 1 : 0;
            });
        }

        return taggedFriends;
    }, [taggedFriends, sortTaggedFriends]);

    const sortedReleaseForms = useMemo(() => {
        if (!releaseForms) return [];

        const documentForms = releaseForms; //.filter((releaseForm: any) => releaseForm.type === 'document');

        if (sortReleaseForms) {
            return [...documentForms].sort((a, b) => {
                const x = a.name.toLowerCase();
                const y = b.name.toLowerCase();
                return x < y ? -1 : x > y ? 1 : 0;
            });
        }

        return documentForms;
    }, [releaseForms, sortReleaseForms]);

    // Memoize handlers
    const handleTabChange = useCallback((event, newValue) => {
        setTabValue(newValue);
        // Close search when switching tabs
        setShowCreatorsSearch(false);
        setShowReleaseFormsSearch(false);
        setCreatorsSearchQuery('');
        setReleaseFormsSearchQuery('');
    }, []);

    const handleCreatorListItemClicked = (id: number) => {
        if (onCreatorListItemClicked) {
            const name = taggedCreators.find(item => item.id === id)?.name ?? taggedFriends?.find(item => item.id === id)?.name;

            if (name) onCreatorListItemClicked({ id, name });

            return;
        }

        const alreadyChecked = taggedCreators.find(item => item.id === id);

        if (alreadyChecked) {
            const newCheckedLists = taggedCreators.filter(item => item.id !== id);
            setTaggedCreators(newCheckedLists);
        } else if (taggedFriends) {
            const listName: string | undefined = taggedFriends.find(item => item.id === id)?.name;

            if (listName) {
                setTaggedCreators([...taggedCreators, { id, name: listName }]);
            }
        }
    };

    const handleReleaseFormListItemClicked = (id: number) => {
        if (onReleaseFormListItemClicked) {
            const name = taggedReleaseForms.find(item => item.id === id)?.name ?? releaseForms?.find(item => item.id === id)?.name;

            if (name) onReleaseFormListItemClicked({ id, name });

            return;
        }

        const alreadyChecked = taggedReleaseForms.find(item => item.id === id);

        if (alreadyChecked) {
            const newCheckedLists = taggedReleaseForms.filter(item => item.id !== id);
            setTaggedReleaseForms(newCheckedLists);
        } else if (releaseForms) {
            const listName: string | undefined = releaseForms.find(item => item.id === id)?.name;

            if (listName) {
                setTaggedReleaseForms([...taggedReleaseForms, { id, name: listName }]);
            }
        }
    };

    const handleDeleteTaggedCreator = (id: number) => {
        const newCheckedLists = taggedCreators.filter(item => item.id !== id);
        setTaggedCreators(newCheckedLists);
    };

    const handleDeleteReleaseForm = (id: number) => {
        const newCheckedLists = taggedReleaseForms.filter(item => item.id !== id);
        setTaggedReleaseForms(newCheckedLists);
    };

    const toggleSortTaggedFriends = useCallback(() => {
        setSortTaggedFriends(prev => !prev);
    }, []);

    const toggleSortReleaseForms = useCallback(() => {
        setSortReleaseForms(prev => !prev);
    }, []);

    const clearTaggedCreators = useCallback(() => {
        setTaggedCreators([]);
    }, [setTaggedCreators]);

    const clearTaggedReleaseForms = useCallback(() => {
        setTaggedReleaseForms([]);
    }, [setTaggedReleaseForms]);

    // Search handlers
    const handleToggleCreatorsSearch = useCallback(() => {
        setShowCreatorsSearch(prev => !prev);
        if (showCreatorsSearch) {
            setCreatorsSearchQuery('');
        }
    }, [showCreatorsSearch]);

    const handleToggleReleaseFormsSearch = useCallback(() => {
        setShowReleaseFormsSearch(prev => !prev);
        if (showReleaseFormsSearch) {
            setReleaseFormsSearchQuery('');
        }
    }, [showReleaseFormsSearch]);

    const handleCreatorsSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setCreatorsSearchQuery(event.target.value);
    }, []);

    const handleReleaseFormsSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setReleaseFormsSearchQuery(event.target.value);
    }, []);

    const handleAddCreatorFromSearch = useCallback(
        (id: number, name: string) => {
            const alreadySelected = taggedCreators.find(item => item.id === id);
            if (!alreadySelected) {
                setTaggedCreators([...taggedCreators, { id, name }]);
            }
        },
        [taggedCreators, setTaggedCreators],
    );

    const handleAddReleaseFormFromSearch = useCallback(
        (id: number, name: string) => {
            const alreadySelected = taggedReleaseForms.find(item => item.id === id);
            if (!alreadySelected) {
                setTaggedReleaseForms([...taggedReleaseForms, { id, name }]);
            }
        },
        [taggedReleaseForms, setTaggedReleaseForms],
    );

    return (
        <>
            <Grid item xs="auto">
                <Chip
                    color={totalTagged === 0 ? 'error' : 'primary'}
                    icon={<CheckCircleIcon />}
                    label={`${totalTagged} selected`}
                    disabled={disabled}
                />
            </Grid>

            <Grid item xs={12}>
                <Tabs variant="fullWidth" value={tabValue} onChange={handleTabChange}>
                    <Tab label="Tag Creators" {...a11yProps(0)} />
                    <Tab label="Tag Release Forms" {...a11yProps(1)} />
                </Tabs>

                <CustomTabPanel value={tabValue} index={0}>
                    <Grid container spacing={1} flexGrow={0} alignItems="center" justifyContent="center">
                        <Grid item xs={12}>
                            <Grid container spacing={1} flexGrow={0} alignItems="center" justifyContent="center">
                                {!taggedFriendsLoading && !taggedFriendsFetching && (
                                    <>
                                        <Grid item xs>
                                            <div style={{ color: theme.palette.grey[500] }}>Select friends to tag</div>
                                        </Grid>
                                        <Grid item xs={'auto'}>
                                            <ActionButtons
                                                onSearch={handleToggleCreatorsSearch}
                                                onSortToggle={toggleSortTaggedFriends}
                                                sortEnabled={sortTaggedFriends}
                                                onClearSelection={clearTaggedCreators}
                                                onRefetch={refetchTaggedFriends}
                                                disabled={disabled}
                                            />
                                        </Grid>
                                    </>
                                )}

                                {/* Creators Search Section */}
                                {(showCreatorsSearch || creatorsSearchQuery.length > 0) && (
                                    <Grid item xs={12}>
                                        <Collapse in={showCreatorsSearch} unmountOnExit={true}>
                                            <Box sx={{ mb: 0 }}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    placeholder="Search creators..."
                                                    value={creatorsSearchQuery}
                                                    onChange={handleCreatorsSearchChange}
                                                    disabled={disabled}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <SearchIcon />
                                                            </InputAdornment>
                                                        ),
                                                        endAdornment:
                                                            creatorsSearchLoading || creatorsSearchFetching ? (
                                                                <InputAdornment position="end">
                                                                    <CircularProgress size={20} />
                                                                </InputAdornment>
                                                            ) : null,
                                                    }}
                                                    sx={{ mt: 0 }}
                                                />
                                                {creatorsSearchQuery.trim() && (
                                                    <Box sx={{ mt: 1 }}>
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            sx={{ mb: 1, display: 'block' }}
                                                        >
                                                            Search Results (click to add):
                                                        </Typography>
                                                        <SearchResultChipsTaggedFriends
                                                            items={creatorsSearchResults || []}
                                                            onAdd={handleAddCreatorFromSearch}
                                                            selectedItems={taggedCreators}
                                                        />
                                                    </Box>
                                                )}
                                            </Box>
                                        </Collapse>
                                    </Grid>
                                )}

                                {taggedFriendsLoading || taggedFriendsFetching ? (
                                    <Grid item xs>
                                        <LoadingIndicator type="Friends" />
                                    </Grid>
                                ) : (
                                    <Grid item xs={12}>
                                        <Grid
                                            container
                                            spacing={2}
                                            flexGrow={1}
                                            alignItems="center"
                                            justifyContent="center"
                                            sx={{ marginBottom: theme.spacing(1) }}
                                        >
                                            <Grid item xs={12}>
                                                <MessageUsersTagCreatorsList
                                                    sortedTaggedFriends={sortedTaggedFriends}
                                                    taggedCreators={taggedCreators}
                                                    handleListItemClicked={handleCreatorListItemClicked}
                                                    theme={theme}
                                                    disabled={disabled}
                                                    indeterminateIds={indeterminateCreatorIds}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>

                        <Grid item xs={12}>
                            <SelectedChips items={taggedCreators} onDelete={handleDeleteTaggedCreator} />
                        </Grid>
                    </Grid>
                </CustomTabPanel>

                <CustomTabPanel value={tabValue} index={1}>
                    <Grid container spacing={1} flexGrow={0} alignItems="center" justifyContent="center">
                        <Grid item xs={12}>
                            <Grid container spacing={1} flexGrow={0} alignItems="center" justifyContent="center">
                                {!releaseFormsLoading && !releaseFormsFetching && (
                                    <>
                                        <Grid item xs>
                                            <div style={{ color: theme.palette.grey[500] }}>Select release forms to tag</div>
                                        </Grid>
                                        <Grid item xs={'auto'}>
                                            <ActionButtons
                                                onSearch={handleToggleReleaseFormsSearch}
                                                onSortToggle={toggleSortReleaseForms}
                                                sortEnabled={sortReleaseForms}
                                                onClearSelection={clearTaggedReleaseForms}
                                                onRefetch={refetchReleaseForms}
                                                disabled={disabled}
                                            />
                                        </Grid>
                                    </>
                                )}

                                {/* Release Forms Search Section */}
                                {(showReleaseFormsSearch || releaseFormsSearchQuery.trim()) && (
                                    <Grid item xs={12}>
                                        <Collapse in={showReleaseFormsSearch} unmountOnExit={true}>
                                            <Box sx={{ mb: 0 }}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    placeholder="Search release forms..."
                                                    value={releaseFormsSearchQuery}
                                                    onChange={handleReleaseFormsSearchChange}
                                                    disabled={disabled}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <SearchIcon />
                                                            </InputAdornment>
                                                        ),
                                                        endAdornment:
                                                            releaseFormsLoading || releaseFormsFetching ? (
                                                                <InputAdornment position="end">
                                                                    <CircularProgress size={20} />
                                                                </InputAdornment>
                                                            ) : null,
                                                    }}
                                                />
                                                {releaseFormsSearchQuery.trim() && (
                                                    <Box sx={{ mt: 1 }}>
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            sx={{ mb: 1, display: 'block' }}
                                                        >
                                                            Search Results:
                                                        </Typography>
                                                        <SearchResultChipsReleaseForms
                                                            items={sortedReleaseForms || []}
                                                            onAdd={handleAddReleaseFormFromSearch}
                                                            selectedItems={taggedReleaseForms}
                                                        />
                                                    </Box>
                                                )}
                                            </Box>
                                        </Collapse>
                                    </Grid>
                                )}

                                {releaseFormsLoading || releaseFormsFetching ? (
                                    <Grid item xs>
                                        <LoadingIndicator type="Release Forms" />
                                    </Grid>
                                ) : (
                                    <Grid item xs={12}>
                                        <Grid
                                            container
                                            spacing={2}
                                            flexGrow={1}
                                            alignItems="center"
                                            justifyContent="center"
                                            sx={{ marginBottom: theme.spacing(1) }}
                                        >
                                            <Grid item xs={12}>
                                                <MessageUsersTagReleaseFormsList
                                                    sortedReleaseForms={sortedReleaseForms}
                                                    taggedReleaseForms={taggedReleaseForms}
                                                    handleListItemClicked={handleReleaseFormListItemClicked}
                                                    theme={theme}
                                                    disabled={disabled}
                                                    indeterminateIds={indeterminateReleaseFormIds}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>

                        <Grid item xs={12}>
                            <SelectedChips items={taggedReleaseForms} onDelete={handleDeleteReleaseForm} />
                        </Grid>
                    </Grid>
                </CustomTabPanel>
            </Grid>
        </>
    );
};

export default OnlyFansTagCreatorsSelector;
